/**
 * ManagePermissionsDialog - lists the active project's members from the
 * backend, lets an owner change roles, remove members and add people found
 * through the assignable-users search. Nothing is written until Save.
 *
 * Member loading, search and the actual mutations are delegated to
 * useProjectMembers - the same hook ProjectMembersPanel uses on the project
 * details page's Members tab - rather than re-implemented here. This dialog
 * only owns the stage-then-save UX on top of it (the Members tab applies
 * each change immediately; this dialog batches edits behind a single Save).
 */
import { useEffect, useMemo, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDialogStyles } from '../../dialogs/dialogStyles';
import { useActiveProject } from '../../activeProject/ActiveProjectContext';
import {
  MIN_ASSIGNABLE_USER_QUERY_LENGTH,
  type AssignableUser,
} from '../../../api/projectMembers';
import { PROJECT_ROLES } from '../../../roles';
import {
  initials,
  isAlreadyMember,
  soleOwnerId as findSoleOwnerId,
} from '../../projects/memberLabels';
import {
  classifyMemberError,
  type MemberErrorKind,
} from '../../projects/memberErrors';
import { useProjectMembers } from '../../projects/useProjectMembers';
import type { ProjectMember, ProjectRole } from '../../projects/types';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Only the project's Owner may hand ownership to someone else. */
  canTransferOwnership?: boolean;
  /** Called once a save has finished writing, before the dialog closes. */
  onSaved?: () => void;
};

const DEFAULT_NEW_ROLE: ProjectRole = 'Collaborator';
const SEARCH_DEBOUNCE_MS = 300;

const useStyles = makeStyles(theme => ({
  memberRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1.75, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  memberAvatar: {
    width: 40,
    height: 40,
    minWidth: 40,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  memberMain: {
    width: 200,
    minWidth: 160,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  memberName: { fontSize: '0.88rem', fontWeight: 600, whiteSpace: 'nowrap' },
  memberEmail: {
    fontSize: '0.76rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  roleGroup: { flex: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' },
}));

const isRole = (value: string): value is ProjectRole =>
  (PROJECT_ROLES as string[]).includes(value);

// Preview mode (ses.projectPreviewMode) is not wired into this dialog - it
// wasn't supported before this change either (see PR known limits), and
// useProjectMembers requires this option regardless of mode.
const previewModeNotSupported = async () => {
  throw new Error('ManagePermissionsDialog does not support preview mode');
};

export function ManagePermissionsDialog({
  open,
  onClose,
  canTransferOwnership = false,
  onSaved,
}: Props) {
  const c = useDialogStyles();
  const classes = useStyles();
  const { t } = useTranslation(['knowledgeBase', 'projects']);
  const { activeProjectId, activeProject } = useActiveProject();

  const projectId = activeProjectId !== null ? String(activeProjectId) : '';

  const {
    members: persistedMembers,
    status,
    reload,
    searchUsers,
    assignRole,
    removeMember: deleteMember,
  } = useProjectMembers({
    projectId,
    isPreviewMode: false,
    savePreviewMembers: previewModeNotSupported,
    // This dialog is mounted unconditionally in DashboardShell - open/onClose
    // only toggle the inner Dialog - so without this the hook's own load
    // effect would fetch project members in the background on every page,
    // not just while the dialog is actually open.
    enabled: open,
  });

  const [staged, setStaged] = useState<ProjectMember[]>([]);
  const [newIds, setNewIds] = useState<ReadonlySet<string>>(new Set());
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [failedNames, setFailedNames] = useState<string[]>([]);
  const [failedKinds, setFailedKinds] = useState<MemberErrorKind[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AssignableUser[]>([]);
  const [transferConfirmPending, setTransferConfirmPending] = useState(false);

  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setTransferConfirmPending(false);
      setFailedNames([]);
      setFailedKinds([]);
    }
  }, [open]);

  // Mirrors the hook's persisted members into the staged copy this dialog
  // edits, every time a (re)load completes - including the reload Save
  // triggers on partial failure, so the list then shows what was actually
  // saved rather than the pre-save attempt. Skipped while saving: every write
  // reloads the members, and resyncing mid-loop would reset the staged edits
  // and flicker the list through partial states. It runs once more when
  // `saving` clears.
  useEffect(() => {
    if (status === 'ready' && !saving) {
      setStaged(persistedMembers);
      setRemovedIds([]);
      setNewIds(new Set());
    }
  }, [status, persistedMembers, saving]);

  useEffect(() => {
    if (
      !open ||
      activeProjectId === null ||
      query.trim().length < MIN_ASSIGNABLE_USER_QUERY_LENGTH
    ) {
      setResults([]);
      setSearchFailed(false);
      return undefined;
    }
    let cancelled = false;
    setSearchFailed(false);
    const timer = setTimeout(async () => {
      try {
        const users = await searchUsers(query.trim());
        if (!cancelled) {
          setResults(users);
        }
      } catch {
        if (!cancelled) {
          setResults([]);
          setSearchFailed(true);
        }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, activeProjectId, query, searchUsers]);

  const visibleMembers = useMemo(
    () => staged.filter(m => !removedIds.includes(m.id)),
    [staged, removedIds],
  );
  // searchUsers already excludes the hook's persisted members; this also
  // excludes anyone staged here but not yet saved (a newly added person, or
  // someone whose removal has not been saved yet should still be offered).
  const candidates = results.filter(r => !isAlreadyMember(visibleMembers, r));

  const persistedRoleById = useMemo(
    () => new Map(persistedMembers.map(m => [m.id, m.role])),
    [persistedMembers],
  );
  const dirty =
    removedIds.length > 0 ||
    visibleMembers.some(
      m => newIds.has(m.id) || persistedRoleById.get(m.id) !== m.role,
    );

  // The project's sole remaining owner can't be removed or demoted here -
  // doing either through this dialog would leave the project ownerless.
  const soleOwnerId = useMemo(
    () => findSoleOwnerId(visibleMembers),
    [visibleMembers],
  );
  const onlyMemberId =
    visibleMembers.length === 1 ? visibleMembers[0].id : undefined;

  // Setting a role to Owner is a full ownership transfer on the backend
  // (it revokes the previous owner), so it needs an explicit confirmation.
  const transfersOwnership = visibleMembers.some(
    m => m.role === 'Owner' && persistedRoleById.get(m.id) !== 'Owner',
  );

  // The backend demotes the current owner when it assigns a new one, so an
  // owner who also demotes or removes themselves in the same save would have
  // that change run first and leave the transfer rejected. While a transfer is
  // staged the persisted owners are therefore locked as they are.
  const lockedOwnerIds = useMemo(
    () =>
      transfersOwnership
        ? persistedMembers.filter(m => m.role === 'Owner').map(m => m.id)
        : [],
    [transfersOwnership, persistedMembers],
  );
  const ownerChangeConflict =
    transfersOwnership &&
    persistedMembers.some(
      m =>
        m.role === 'Owner' &&
        !visibleMembers.some(v => v.id === m.id && v.role === 'Owner'),
    );
  const ownerless =
    visibleMembers.length > 0 && !visibleMembers.some(m => m.role === 'Owner');

  // A project has one Owner, so only one person can be staged to receive it.
  const stagedOwnerCount = visibleMembers.filter(
    m => m.role === 'Owner' && persistedRoleById.get(m.id) !== 'Owner',
  ).length;

  const isLocked = (id: string) =>
    id === soleOwnerId || id === onlyMemberId || lockedOwnerIds.includes(id);

  const setRole = (id: string, role: ProjectRole) => {
    if (isLocked(id)) return;
    if (
      role === 'Owner' &&
      persistedRoleById.get(id) !== 'Owner' &&
      (!canTransferOwnership || stagedOwnerCount > 0)
    ) {
      return;
    }
    setTransferConfirmPending(false);
    setStaged(list => list.map(m => (m.id === id ? { ...m, role } : m)));
  };

  const addPerson = (person: AssignableUser) => {
    setTransferConfirmPending(false);
    setRemovedIds(list => list.filter(id => id !== person.id));
    setStaged(list =>
      list.some(m => m.id === person.id)
        ? list
        : [...list, { ...person, role: DEFAULT_NEW_ROLE }],
    );
    setNewIds(ids => new Set(ids).add(person.id));
    setQuery('');
  };

  const removeMember = (member: ProjectMember) => {
    if (isLocked(member.id)) return;
    setTransferConfirmPending(false);
    if (newIds.has(member.id)) {
      setStaged(list => list.filter(m => m.id !== member.id));
      setNewIds(ids => {
        const next = new Set(ids);
        next.delete(member.id);
        return next;
      });
    } else {
      setRemovedIds(list => [...list, member.id]);
    }
  };

  const save = async () => {
    if (
      activeProjectId === null ||
      ownerChangeConflict ||
      ownerless ||
      stagedOwnerCount > 1
    ) {
      return;
    }
    setSaving(true);
    setFailedNames([]);
    setFailedKinds([]);
    const failed: string[] = [];
    const kinds = new Set<MemberErrorKind>();

    const steps: Array<{ label: string; action: () => Promise<unknown> }> = [];
    for (const id of removedIds) {
      const member =
        staged.find(m => m.id === id) ??
        persistedMembers.find(m => m.id === id);
      steps.push({ label: member?.name ?? id, action: () => deleteMember(id) });
    }
    const changed = visibleMembers.filter(
      m => newIds.has(m.id) || persistedRoleById.get(m.id) !== m.role,
    );
    // Setting an Owner revokes the previous owner on the backend, so the
    // transfer is written last: any write after it, and the reload that
    // follows, would be rejected for the caller who just lost access.
    for (const m of [
      ...changed.filter(x => x.role !== 'Owner'),
      ...changed.filter(x => x.role === 'Owner'),
    ]) {
      steps.push({
        label: m.name,
        action: () =>
          assignRole({ id: m.id, name: m.name, email: m.email }, m.role),
      });
    }

    // The writes are separate requests, so the save cannot be atomic. It
    // stops at the first failure instead of pressing on: later changes (above
    // all the ownership transfer) may depend on the earlier ones.
    for (const step of steps) {
      try {
        await step.action();
      } catch (err) {
        failed.push(step.label);
        kinds.add(classifyMemberError(err));
        break;
      }
    }

    onSaved?.();
    if (failed.length === 0) {
      setSaving(false);
      onClose();
      return;
    }
    // Reload so the list shows what the backend actually holds.
    await reload();
    setFailedNames(failed);
    setFailedKinds([...kinds]);
    setSaving(false);
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSaveClick = () => {
    if (transfersOwnership && !transferConfirmPending) {
      setTransferConfirmPending(true);
      return;
    }
    setTransferConfirmPending(false);
    void save();
  };

  const loading = status === 'loading';
  const loadFailed = status === 'error';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography className={c.title}>
        <span className={c.titleTextWrap}>
          <span className={c.titleText}>{t('dialogs.permissions')}</span>
          <span className={c.titleSub}>
            {activeProject
              ? t('dialogs.permissionsForProject', { name: activeProject.name })
              : t('dialogs.permissionsDescription')}
          </span>
        </span>
        <IconButton
          size="small"
          className={c.closeBtn}
          aria-label={t('dialogs.close')}
          disabled={saving}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className={c.body}>
        {activeProjectId === null && (
          <Typography color="textSecondary">
            {t('dialogs.permissionsNoProject')}
          </Typography>
        )}

        {activeProjectId !== null && loading && <CircularProgress size={24} />}

        {failedKinds.map(kind => (
          <Typography key={kind} color="error" role="alert">
            {t(`projects:members.errors.${kind}`)}
          </Typography>
        ))}

        {failedNames.length > 0 && (
          <Typography color="textSecondary" variant="body2">
            {t('dialogs.permissionsSaveStopped')}
          </Typography>
        )}

        {(loadFailed || failedNames.length > 0) && (
          <Typography color="error" role="alert">
            {loadFailed
              ? t('dialogs.permissionsLoadError')
              : t('dialogs.permissionsSaveError', {
                  names: failedNames.join(', '),
                })}
          </Typography>
        )}

        {searchFailed && (
          <Typography color="error" role="alert">
            {t('dialogs.permissionsSearchError')}
          </Typography>
        )}

        {(ownerChangeConflict || ownerless) && (
          <Typography color="error" role="alert">
            {t('dialogs.ownerChangeConflict')}
          </Typography>
        )}

        {transferConfirmPending && (
          <Typography color="error" role="alert">
            {t('projects:members.transferDescription')}
          </Typography>
        )}

        {activeProjectId !== null && !loading && (
          <>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={t('dialogs.searchUsers')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {query.trim().length >= MIN_ASSIGNABLE_USER_QUERY_LENGTH &&
              candidates.length === 0 && (
                <Typography color="textSecondary" variant="body2">
                  {t('dialogs.noUsersFound')}
                </Typography>
              )}
            {candidates.map(person => (
              <div key={person.id} className={classes.memberRow}>
                <span className={classes.memberMain}>
                  <span className={classes.memberName}>{person.name}</span>
                  <span className={classes.memberEmail}>{person.email}</span>
                </span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => addPerson(person)}
                >
                  {t('dialogs.addMember')}
                </Button>
              </div>
            ))}

            {visibleMembers.length === 0 && !loadFailed && (
              <Typography color="textSecondary">
                {t('dialogs.noMembers')}
              </Typography>
            )}
            {visibleMembers.map(m => {
              const isProtected = isLocked(m.id);
              return (
                <div key={m.id} className={classes.memberRow}>
                  <span className={classes.memberAvatar} aria-hidden="true">
                    {initials(m.name)}
                  </span>
                  <span className={classes.memberMain}>
                    <span className={classes.memberName}>{m.name}</span>
                    <span className={classes.memberEmail}>{m.email}</span>
                  </span>
                  <Tooltip
                    title={isProtected ? t('dialogs.onlyOwnerProtected') : ''}
                  >
                    <RadioGroup
                      row
                      className={classes.roleGroup}
                      value={m.role}
                      onChange={e => {
                        if (isRole(e.target.value)) {
                          setRole(m.id, e.target.value);
                        }
                      }}
                    >
                      {PROJECT_ROLES.map(r => (
                        <FormControlLabel
                          key={r}
                          value={r}
                          disabled={
                            isProtected ||
                            (r === 'Owner' &&
                              m.role !== 'Owner' &&
                              persistedRoleById.get(m.id) !== 'Owner' &&
                              (!canTransferOwnership || stagedOwnerCount > 0))
                          }
                          control={<Radio color="primary" size="small" />}
                          label={t(`dialogs.roles.${r.toLowerCase()}`)}
                        />
                      ))}
                    </RadioGroup>
                  </Tooltip>
                  <Tooltip
                    title={
                      isProtected
                        ? t('dialogs.onlyOwnerProtected')
                        : t('dialogs.removeMember')
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={isProtected}
                        aria-label={t('dialogs.removeMember')}
                        onClick={() => removeMember(m)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>
              );
            })}
          </>
        )}
      </DialogContent>

      <DialogActions className={c.actions}>
        <Button disabled={saving} onClick={handleClose}>
          {t('dialogs.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={
            !dirty ||
            saving ||
            activeProjectId === null ||
            ownerChangeConflict ||
            ownerless ||
            stagedOwnerCount > 1
          }
          onClick={handleSaveClick}
        >
          {transferConfirmPending
            ? t('projects:members.transferConfirm')
            : t('dialogs.savePermissions')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
